/*
 * Copyright 2026 North Carolina State University
 */

import React from 'react';
import { useTimeFormat, to24Hour, from24Hour } from '../../utils/timeFormat';

const pad = (n) => String(n).padStart(2, '0');
const HOURS_12 = Array.from({ length: 12 }, (_, i) => pad(i + 1));
const HOURS_24 = Array.from({ length: 24 }, (_, i) => pad(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad(i));

/**
 * Hour / minute (/ AM-PM) dropdowns for a time kept as 12-hour parts —
 * { hour: '01'-'12', minute: '00'-'59', period: 'AM'|'PM' }, each of which may
 * be 'N/A' — as the application's lighting schedule stores it ("06:00 AM").
 *
 * In 24-hour mode the user picks 00-23 and there is no AM/PM box, but the
 * same 12-hour parts are written back, so saved data never changes shape.
 * `onChange` always receives all three parts.
 */
const ClockSelect = ({ hour, minute, period, defaultHour = 'N/A', onChange }) => {
  const format = useTimeFormat();

  const h = hour === undefined || hour === null || hour === '' ? defaultHour : String(hour);
  const isNA = h === 'N/A';
  const hour12 = isNA ? 'N/A' : pad(Number(h));
  const min = isNA ? 'N/A' : (minute === undefined || minute === null || minute === '' || minute === 'N/A' ? '00' : pad(Number(minute)));
  const per = isNA ? 'N/A' : (period === 'PM' ? 'PM' : 'AM');

  // Leaving N/A starts the minutes at :00, as the old per-field handler did.
  const set = (next) => onChange({ hour: hour12, minute: min, period: per, ...next });
  const clear = () => onChange({ hour: 'N/A', minute: 'N/A', period: 'N/A' });

  const onHour = (raw) => {
    if (raw === 'N/A') return clear();
    if (format === '24h') return set({ ...from24Hour(Number(raw)), minute: isNA ? '00' : min });
    return set({ hour: raw, minute: isNA ? '00' : min, period: isNA ? 'AM' : per });
  };

  return (
    <div className="time-input">
      <select
        aria-label="Hour"
        value={isNA ? 'N/A' : (format === '24h' ? pad(to24Hour(hour12, per)) : hour12)}
        onChange={(e) => onHour(e.target.value)}
      >
        <option value="N/A">N/A</option>
        {(format === '24h' ? HOURS_24 : HOURS_12).map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      :
      <select
        aria-label="Minute"
        value={min}
        onChange={(e) => set({ minute: e.target.value })}
        disabled={isNA}
      >
        <option value="N/A">N/A</option>
        {MINUTES.map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      {format === '12h' && (
        <select
          aria-label="AM or PM"
          value={per}
          onChange={(e) => set({ period: e.target.value })}
          disabled={isNA}
        >
          <option value="N/A">N/A</option>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      )}
    </div>
  );
};

export default ClockSelect;
